import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosClient, getBaseUrlVersioned } from "../../../services/axiosClient";
import { localStorageService } from "../../../services/index";
import { API_VERSION } from "../../types";
import { FetchStatus } from "./types";
export const CHARACTER_RATINGS_KEY = "character_ratings";
export const LASTTIME_FILTERED_CHARACTERS_KEY = "lastTimeFilteredCharacters";
const API_USER_SETTINGS = "users/settings";
/**
 * Thunk action to get user Settings .
 * Makes an HTTP GET request to fetch the user Settings .
 */
export const getUserSettings = createAsyncThunk("users/settings", async () => {
    const apiUrl = `${getBaseUrlVersioned(null)}/${API_USER_SETTINGS}`;
    const response = await axiosClient.get(apiUrl);
    return response.data;
});
export const updateUserSettings = createAsyncThunk("userSettings/updateUserSettings", async (userSettings) => {
    const apiUrl = `${getBaseUrlVersioned(API_VERSION.V2)}/${API_USER_SETTINGS}`;
    await axiosClient.patch(apiUrl, userSettings);
});
export const likeBot = createAsyncThunk("userSettings/likeBot", async (id, thunkAPI) => {
    if (!id) {
        throw new Error("Id is required to like/unlike a bot");
    }
    const state = (await thunkAPI.getState());
    const currentLikedBots = [...(state.userSettings?.liked_bots || [])];
    const isAlreadyLiked = currentLikedBots.includes(id);
    const payload = isAlreadyLiked ? { unlikedBot: id } : { likedBot: id };
    const newLikedBots = isAlreadyLiked
        ? currentLikedBots.filter((botId) => botId !== id)
        : [...currentLikedBots, id];
    await thunkAPI.dispatch(updateUserSettings(payload)).unwrap();
    return newLikedBots;
});
export const likeLorebook = createAsyncThunk("userSettings/likeLorebook", async (id, thunkAPI) => {
    if (!id) {
        throw new Error("Id is required to like/unlike a lorebook");
    }
    const state = (await thunkAPI.getState());
    const currentLikedLorebooks = [
        ...(state.userSettings?.liked_lorebooks || []),
    ];
    const isAlreadyLiked = currentLikedLorebooks.includes(id);
    const payload = isAlreadyLiked
        ? { unlikedLorebook: id }
        : { likedLorebook: id };
    const newLikedLorebooks = isAlreadyLiked
        ? currentLikedLorebooks.filter((lorebookId) => lorebookId !== id)
        : [...currentLikedLorebooks, id];
    await thunkAPI.dispatch(updateUserSettings(payload)).unwrap();
    return newLikedLorebooks;
});
export const blockCreator = createAsyncThunk("userSettings/blockCreator", async (id, thunkAPI) => {
    const state = (await thunkAPI.getState());
    const currentBlockedUsers = [
        ...(state.userSettings?.blocked_users || []),
    ];
    if (currentBlockedUsers.includes(id)) {
        return currentBlockedUsers;
    }
    const newBlockedUsers = [...currentBlockedUsers, id];
    await thunkAPI.dispatch(updateUserSettings({ blockUser: id })).unwrap();
    return newBlockedUsers;
});
export const unblockCreator = createAsyncThunk("userSettings/unblockCreator", async (id, thunkAPI) => {
    const state = (await thunkAPI.getState());
    const currentBlockedUsers = [
        ...(state.userSettings?.blocked_users || []),
    ];
    const newBlockedUsers = currentBlockedUsers.filter((userId) => userId !== id);
    await thunkAPI.dispatch(updateUserSettings({ unblockUser: id })).unwrap();
    return newBlockedUsers;
});
export const updateAutoMemory = createAsyncThunk("userSettings/updateAutoMemory", async (autoMemoryEnabled, thunkAPI) => {
    await thunkAPI
        .dispatch(updateUserSettings({ auto_memory_enabled: autoMemoryEnabled }))
        .unwrap();
    return autoMemoryEnabled;
});
export const updateAutoResponse = createAsyncThunk("userSettings/updateAutoResponse", async (auto_response_enabled, thunkAPI) => {
    await thunkAPI
        .dispatch(updateUserSettings({ auto_response_enabled }))
        .unwrap();
    return auto_response_enabled;
});
export const updateBlurNsfw = createAsyncThunk("userSettings/updateBlurNsfw", async (blur_nsfw, thunkAPI) => {
    await thunkAPI
        .dispatch(updateUserSettings({ blurNSFW: blur_nsfw }))
        .unwrap();
    return blur_nsfw;
});
export const updateShowNsfw = createAsyncThunk("userSettings/updateShowNsfw", async (show_nsfw, thunkAPI) => {
    await thunkAPI
        .dispatch(updateUserSettings({ showNSFW: show_nsfw }))
        .unwrap();
    return show_nsfw;
});
export const updateNSFWFilterEnabled = createAsyncThunk("userSettings/updateNSFWFilterEnabled", async (nsfw_filter_enabled, thunkAPI) => {
    await thunkAPI
        .dispatch(updateUserSettings({ nsfw_filter_enabled }))
        .unwrap();
    return nsfw_filter_enabled;
});
export const updateSelectedTags = createAsyncThunk("userSettings/updateSelectedTags", async (selected_tags, thunkAPI) => {
    await thunkAPI.dispatch(updateUserSettings({ selected_tags })).unwrap();
    return selected_tags;
});
export const updateSelectedLanguages = createAsyncThunk("userSettings/updateSelectedLanguages", async (selected_languages, thunkAPI) => {
    await thunkAPI
        .dispatch(updateUserSettings({ selected_languages }))
        .unwrap();
    return selected_languages;
});
export const updateSelectedChatSizes = createAsyncThunk("userSettings/updateSelectedChatSizes", async (selected_chat_sizes, thunkAPI) => {
    await thunkAPI
        .dispatch(updateUserSettings({ selected_chat_sizes }))
        .unwrap();
    return selected_chat_sizes;
});
export const updateSelectedDefinitions = createAsyncThunk("userSettings/updateSelectedDefinitions", async (selected_definitions, thunkAPI) => {
    await thunkAPI
        .dispatch(updateUserSettings({ selected_definitions }))
        .unwrap();
    return selected_definitions;
});
export const updateTtsIncludeNarration = createAsyncThunk("userSettings/updateTtsIncludeNarration", async (tts_include_narration, thunkAPI) => {
    await thunkAPI
        .dispatch(updateUserSettings({ ttsIncludeNarration: tts_include_narration }))
        .unwrap();
    return tts_include_narration;
});
export const updateChatLanguage = createAsyncThunk("userSettings/updateChatLanguage", async (chat_language, thunkAPI) => {
    await thunkAPI
        .dispatch(updateUserSettings({ chatLanguage: chat_language }))
        .unwrap();
    return chat_language;
});
export const updateNSFWSettings = createAsyncThunk("userSettings/updateNSFWSettings", async ({ showNSFW, blurNSFW }, thunkAPI) => {
    const payload = blurNSFW !== undefined ? { showNSFW, blurNSFW } : { showNSFW };
    await thunkAPI.dispatch(updateUserSettings(payload)).unwrap();
    return blurNSFW !== undefined ? { showNSFW, blurNSFW } : { showNSFW };
});
// Generation Presets async thunks
export const createGenerationPreset = createAsyncThunk("userSettings/createGenerationPreset", async (input) => {
    const apiUrl = `${getBaseUrlVersioned(null)}/${API_USER_SETTINGS}/generation-presets`;
    const response = await axiosClient.post(apiUrl, input);
    return response.data;
});
export const updateGenerationPreset = createAsyncThunk("userSettings/updateGenerationPreset", async ({ presetId, updates, }) => {
    const apiUrl = `${getBaseUrlVersioned(null)}/${API_USER_SETTINGS}/generation-presets/${presetId}`;
    const response = await axiosClient.patch(apiUrl, updates);
    return response.data;
});
export const deleteGenerationPreset = createAsyncThunk("userSettings/deleteGenerationPreset", async (presetId) => {
    const apiUrl = `${getBaseUrlVersioned(null)}/${API_USER_SETTINGS}/generation-presets/${presetId}`;
    await axiosClient.delete(apiUrl);
    return presetId;
});
const initialState = {
    isAutoMemoryEnabled: true,
    auto_response_enabled: false,
    are_user_settings_loading: FetchStatus.idle,
    are_user_settings_loaded: false,
    blocked_users: new Array(),
    liked_bots: new Array(),
    liked_lorebooks: new Array(),
    selected_tags: new Array(),
    selected_chat_sizes: new Array(),
    selected_definitions: new Array(),
    selected_languages: new Array(),
    blur_nsfw: false,
    show_nsfw: false,
    new_profile: false,
    character_ratings: {},
    last_time_filtered_characters: undefined,
    first_time_waiting_modal_visible: false,
    first_time_onboard_banner_visible: true,
    remember_selected_persona_modal: false,
    avatar_generation_time: null,
    is_loading: false,
    show_unsaved_profile_changes_warning: false,
    nsfw_filter_enabled: false,
    tts_include_narration: false,
    chat_language: "en",
    generation_presets: [],
};
const localSetLikedBots = (state, liked_bots) => {
    let likedBots;
    if (!liked_bots) {
        likedBots = new Array();
    }
    else {
        likedBots = liked_bots;
    }
    state.liked_bots = likedBots;
};
const localSetLikedLorebooks = (state, liked_lorebooks) => {
    state.liked_lorebooks = liked_lorebooks ?? new Array();
};
const localSetBlockUsers = (state, block_users) => {
    let blockUsers;
    if (!block_users) {
        blockUsers = new Array();
    }
    else {
        blockUsers = block_users;
    }
    state.blocked_users = blockUsers;
};
const localSetSelectedTags = (state, selected_tags) => {
    let selectedTags;
    if (!selected_tags) {
        selectedTags = new Array();
    }
    else {
        selectedTags = selected_tags;
    }
    state.selected_tags = selectedTags;
};
const localSetSelectedLanguages = (state, selected_languages) => {
    let selectedLanguages;
    if (!selected_languages) {
        selectedLanguages = new Array();
    }
    else {
        selectedLanguages = selected_languages;
    }
    state.selected_languages = selectedLanguages;
};
const localSetSelectedChatSizes = (state, selected_chat_sizes) => {
    let selectedChatSizes;
    if (!selected_chat_sizes) {
        selectedChatSizes = new Array();
    }
    else {
        selectedChatSizes = selected_chat_sizes;
    }
    state.selected_chat_sizes = selectedChatSizes;
};
const localSetSelectedDefinitions = (state, selected_definitions) => {
    let selectedDefinitions;
    if (!selected_definitions) {
        selectedDefinitions = new Array();
    }
    else {
        selectedDefinitions = selected_definitions;
    }
    state.selected_definitions = selectedDefinitions;
};
export const userSettingsSlice = createSlice({
    name: "userSettings",
    initialState,
    reducers: {
        setLikedBots: (state, action) => {
            localSetLikedBots(state, action.payload);
        },
        setLikedLorebooks: (state, action) => {
            localSetLikedLorebooks(state, action.payload);
        },
        setBlockedUsers: (state, action) => {
            localSetBlockUsers(state, action.payload);
        },
        setSelectedTags: (state, action) => {
            localSetSelectedTags(state, action.payload);
        },
        setSelectedDefinitions: (state, action) => {
            localSetSelectedDefinitions(state, action.payload);
        },
        setSelectedLanguages: (state, action) => {
            localSetSelectedLanguages(state, action.payload);
        },
        setSelectedChatSizes: (state, action) => {
            localSetSelectedChatSizes(state, action.payload);
        },
        setCharacterRating: (state, action) => {
            const { characterId, rating } = action.payload;
            state.character_ratings[characterId] = rating;
            localStorageService.setItem(CHARACTER_RATINGS_KEY, JSON.stringify(state.character_ratings));
        },
        setCharactersRatings: (state, action) => {
            state.character_ratings = action.payload;
        },
        setUserLastTimeFilteredCharacters: (state, action) => {
            state.last_time_filtered_characters = action.payload;
            localStorageService.setItem(LASTTIME_FILTERED_CHARACTERS_KEY, action.payload.toString());
        },
        setAutoResponse: (state, action) => {
            state.auto_response_enabled = !!action.payload;
        },
        setShowNSFW: (state, action) => {
            state.show_nsfw = !!action.payload;
        },
        setNSFWFilterEnabled: (state, action) => {
            state.nsfw_filter_enabled = !!action.payload;
        },
        setBlurNSFW: (state, action) => {
            state.blur_nsfw = !!action.payload;
        },
        updateNewProfile: (state, action) => {
            state.new_profile = !!action.payload;
        },
        setShowUnsavedProfileChangesWarning: (state, action) => {
            state.show_unsaved_profile_changes_warning = action.payload;
        },
        setIsUserSettingsLoaded: (state, action) => {
            state.are_user_settings_loaded = !!action.payload;
        },
        setUserFirstTimeWaitingModalVisible: (state, action) => {
            state.first_time_waiting_modal_visible = action.payload;
        },
        setUserFirstTimeOnboardBannerVisible: (state, action) => {
            state.first_time_onboard_banner_visible = action.payload;
        },
        setUserRememberSelectedPersonaModal: (state, action) => {
            state.remember_selected_persona_modal = action.payload;
        },
        setUserAvatarGenerationTime: (state, action) => {
            state.avatar_generation_time = action.payload;
        },
        setResetUserSettings: (state) => {
            state.isAutoMemoryEnabled = true;
            state.auto_response_enabled = false;
            state.liked_bots = [];
            state.liked_lorebooks = [];
            state.blocked_users = [];
            state.selected_tags = [];
            state.selected_languages = [];
            state.selected_chat_sizes = [];
            state.selected_definitions = [];
            state.show_nsfw = false;
            state.blur_nsfw = false;
            state.are_user_settings_loaded = false;
            state.nsfw_filter_enabled = false;
            state.tts_include_narration = false;
            state.chat_language = "EN";
            state.generation_presets = [];
        },
        setTtsIncludeNarration: (state, action) => {
            state.tts_include_narration = !!action.payload;
        },
        setChatLanguage: (state, action) => {
            state.chat_language = action.payload || "EN";
        },
        setGenerationPresets: (state, action) => {
            state.generation_presets = action.payload || [];
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getUserSettings.pending, (state) => {
            state.are_user_settings_loading = FetchStatus.loading;
        })
            .addCase(getUserSettings.fulfilled, (state, action) => {
            localSetBlockUsers(state, action.payload.blocked_users);
            localSetSelectedTags(state, action.payload.selected_tags);
            localSetSelectedLanguages(state, action.payload.selected_languages);
            localSetSelectedChatSizes(state, action.payload.selected_chat_sizes);
            localSetSelectedDefinitions(state, action.payload.selected_definitions);
            localSetLikedBots(state, action.payload.liked_bots);
            localSetLikedLorebooks(state, action.payload.liked_lorebooks);
            state.isAutoMemoryEnabled =
                action.payload.auto_memory_enabled ?? true;
            state.auto_response_enabled = !!action.payload.auto_response_enabled;
            state.show_nsfw = !!action.payload.show_nsfw;
            state.blur_nsfw = !!action.payload.blur_nsfw;
            state.are_user_settings_loading = FetchStatus.succeeded;
            state.are_user_settings_loaded = true;
            state.nsfw_filter_enabled = !!action.payload.nsfw_filter_enabled;
            state.tts_include_narration = !!action.payload.tts_include_narration;
            state.chat_language = action.payload.chat_language;
            state.generation_presets = action.payload.generation_presets || [];
        })
            .addCase(getUserSettings.rejected, (state) => {
            state.are_user_settings_loaded = false;
            state.are_user_settings_loading = FetchStatus.failed;
        })
            .addCase(likeBot.fulfilled, (state, action) => {
            const id = action.meta.arg;
            const wasLikeIntent = action.payload.includes(id);
            if (wasLikeIntent) {
                if (!state.liked_bots.includes(id)) {
                    state.liked_bots = [...state.liked_bots, id];
                }
            }
            else {
                state.liked_bots = state.liked_bots.filter((botId) => botId !== id);
            }
        })
            .addCase(likeLorebook.fulfilled, (state, action) => {
            const id = action.meta.arg;
            const currentLikedLorebooks = state.liked_lorebooks ?? [];
            const wasLikeIntent = action.payload.includes(id);
            if (wasLikeIntent) {
                if (!currentLikedLorebooks.includes(id)) {
                    state.liked_lorebooks = [...currentLikedLorebooks, id];
                }
            }
            else {
                state.liked_lorebooks = currentLikedLorebooks.filter((lorebookId) => lorebookId !== id);
            }
        })
            .addCase(blockCreator.fulfilled, (state, action) => {
            const id = action.meta.arg;
            if (!state.blocked_users.includes(id)) {
                state.blocked_users = [...state.blocked_users, id];
            }
        })
            .addCase(unblockCreator.fulfilled, (state, action) => {
            const id = action.meta.arg;
            state.blocked_users = state.blocked_users.filter((userId) => userId !== id);
        })
            .addCase(updateAutoMemory.fulfilled, (state, action) => {
            state.isAutoMemoryEnabled = action.payload ?? true;
        })
            .addCase(updateAutoResponse.fulfilled, (state, action) => {
            state.auto_response_enabled = !!action.payload;
        })
            .addCase(updateBlurNsfw.fulfilled, (state, action) => {
            state.blur_nsfw = !!action.payload;
        })
            .addCase(updateShowNsfw.fulfilled, (state, action) => {
            state.show_nsfw = !!action.payload;
        })
            .addCase(updateNSFWFilterEnabled.fulfilled, (state, action) => {
            state.nsfw_filter_enabled = !!action.payload;
        })
            .addCase(updateSelectedTags.fulfilled, (state, action) => {
            localSetSelectedTags(state, action.payload);
        })
            .addCase(updateSelectedLanguages.fulfilled, (state, action) => {
            localSetSelectedLanguages(state, action.payload);
        })
            .addCase(updateSelectedChatSizes.fulfilled, (state, action) => {
            localSetSelectedChatSizes(state, action.payload);
        })
            .addCase(updateSelectedDefinitions.fulfilled, (state, action) => {
            localSetSelectedDefinitions(state, action.payload);
        })
            .addCase(updateTtsIncludeNarration.fulfilled, (state, action) => {
            state.tts_include_narration = !!action.payload;
        })
            .addCase(updateChatLanguage.fulfilled, (state, action) => {
            state.chat_language = action.payload;
        })
            .addCase(updateNSFWSettings.fulfilled, (state, action) => {
            state.show_nsfw = !!action.payload.showNSFW;
            if (action.payload.blurNSFW !== undefined) {
                state.blur_nsfw = !!action.payload.blurNSFW;
            }
        })
            .addCase(updateUserSettings.pending, (state) => {
            state.is_loading = true;
        })
            .addCase(updateUserSettings.rejected, (state) => {
            state.is_loading = false;
        })
            .addCase(updateUserSettings.fulfilled, (state) => {
            state.is_loading = false;
        })
            .addCase(createGenerationPreset.fulfilled, (state, action) => {
            state.generation_presets.push(action.payload);
        })
            .addCase(updateGenerationPreset.fulfilled, (state, action) => {
            const index = state.generation_presets.findIndex((preset) => preset.id === action.payload.id);
            if (index !== -1) {
                state.generation_presets[index] = action.payload;
            }
        })
            .addCase(deleteGenerationPreset.fulfilled, (state, action) => {
            state.generation_presets = state.generation_presets.filter((preset) => preset.id !== action.payload);
        });
    },
});
export const { setAutoResponse, setLikedBots, setLikedLorebooks, setCharacterRating, setCharactersRatings, setUserLastTimeFilteredCharacters, setSelectedTags, setShowNSFW, setBlurNSFW, setBlockedUsers, updateNewProfile, setShowUnsavedProfileChangesWarning, setIsUserSettingsLoaded, setUserFirstTimeWaitingModalVisible, setUserFirstTimeOnboardBannerVisible, setUserRememberSelectedPersonaModal, setUserAvatarGenerationTime, setResetUserSettings, setNSFWFilterEnabled, setTtsIncludeNarration, setChatLanguage, setSelectedLanguages, setSelectedChatSizes, setSelectedDefinitions, setGenerationPresets, } = userSettingsSlice.actions;
export default userSettingsSlice.reducer;
